import { useFormik } from 'formik'
import { useState } from 'react'
import {
    dall_e2SizesOptions,
    imageModelsOptions,
    openAiTextModelsOptions,
    OptionType,
    providersOptions,
    qualityOptions,
    responseOptions,
    samplersOptions,
    styleOptions,
} from './types'
// import { header } from '../../constants'

import {
    applyDefaults,
    handleStreamingResponse,
    useHandleResponseError,
    useAbortController,
} from '../../utils'

import useChatHistory from '../../components/History/useChatHistory'
import useImagesHistory from '../../components/History/useImagesHistory'
import fetchToCurl from 'fetch-to-curl'
import useScrollToBottom from 'utils/useScrollToBottom'
import { useFetchAndGenerateUploadUrlService } from 'services/useFileService'
import { getRequestHeaders } from '../../requestHeder'

export const useCortext = () => {
    const { handleResponseError } = useHandleResponseError()
    const { createAbortController, abort } = useAbortController()
    const { fetchAndGenerateUploadUrlService } = useFetchAndGenerateUploadUrlService()

    const { scrollRef, scrollToBottom } = useScrollToBottom()

    const [responseType, setResponseType] = useState<OptionType>(responseOptions[0])

    const [displayPrompt, setDisplayPrompt] = useState('')

    const {
        chat,
        chatHistory,
        updateMessagesAndHistory,
        handleStartNewChat,
        setChat,
        handleDeleteHistory,
    } = useChatHistory()

    const [isLoading, setIsLoading] = useState(false)
    const [isStreaming, setIsStreaming] = useState(false)

    const { image, saveImage, uploadBase64Image } = useImagesHistory()

    const initialValues = {
        streamingResponse: true,

        //main
        message: '',
        provider: providersOptions[0].value,
        model: openAiTextModelsOptions[0].value,
        seed: 1234,
        temperature: 0.5,
        max_tokens: 2048,
        top_p: 0.8,
        top_k: 1000,
        timeout: 30,

        //ImageResponse
        samples: 1,
        cfg_scale: 8,
        sampler: samplersOptions[0].value,
        steps: 45,
        style: styleOptions[0].value,
        size: dall_e2SizesOptions[0].value,
        height: 1024,
        width: 1024,
        quality: qualityOptions[0].value,
        image_model: imageModelsOptions[0].value,
        //isAlive
        answer: '',
    }

    const request_headers = getRequestHeaders()

    const handleSubmit = async (values: any, signal: AbortSignal) => {
        try {
            const finalValues = applyDefaults(values, initialValues)

            if (responseType.value === 'StreamPrompting') {
                updateMessagesAndHistory(finalValues.message, 'You')
            }

            if (finalValues.message.trim()) {
                const query = `${import.meta.env.REACT_APP_SUBNET_API}/cortex-text-generation`

                const historyMessages = chat?.messages?.map(chat => {
                    return {
                        role: chat.name === 'You' ? 'user' : 'assistant',
                        content:
                            chat.message.length > 200
                                ? `${chat.message.slice(0, 200)}...`
                                : chat.message, // Truncate if too long
                    }
                })

                const finalMessages = [
                    {
                        role: 'system',
                        content: `Chat History: ${historyMessages
                            ?.slice(-4)
                            ?.map(msg => msg.content)
                            ?.join('\n')}
                          current message: ${finalValues.message}`,
                    },
                    {
                        role: 'user',
                        content: finalValues.message,
                    },
                ]

                const options = {
                    method: 'POST',
                    headers: request_headers as any,
                    body: JSON.stringify({
                        messages: finalMessages,
                        provider: finalValues.provider,
                        model: finalValues.model,
                        seed: finalValues.seed,
                        temperature: finalValues.temperature,
                        max_tokens: finalValues.max_tokens,
                        top_p: finalValues.top_p,
                        top_k: finalValues.top_k,
                        timeout: finalValues.timeout,
                    }),
                    signal,
                }

                const curlString = fetchToCurl(query, options)?.replace(/\$/g, '\\$')

                const response: any = await fetch(query, options)

                handleResponseError(response, () => {
                    setIsLoading(false)
                    setIsStreaming(false)
                })

                if (!response.body) return

                if (responseType.value === 'StreamPrompting') {
                    setIsStreaming(true)
                    handleStreamingResponse(
                        response,
                        (text: string) => {
                            updateMessagesAndHistory(text, finalValues.model, true, curlString)
                        },
                        () => {
                            setIsStreaming(false)
                        },
                    )
                } else {
                    const data = await response.json()

                    if (responseType.value === 'ImageResponse') {
                        const imageResponse = data?.response?.[0]

                        const encodedUrl = encodeURIComponent(imageResponse?.completion?.url)

                        const fetchedBase64 = await fetchAndGenerateUploadUrlService(encodedUrl)

                        const uploadedUrl = await uploadBase64Image(fetchedBase64.content)

                        if (!imageResponse?.completion) return
                        const newImage = {
                            style: imageResponse?.style,
                            width: imageResponse?.width,
                            height: imageResponse?.height,
                            model: imageResponse?.model,
                            prompt: imageResponse?.messages,
                            url: uploadedUrl,
                            cUrl: curlString,
                        }
                        saveImage(newImage)
                    }
                }
            }
        } catch (e) {
            console.log(e)
        }
    }

    const formik = useFormik({
        initialValues: initialValues,
        onSubmit: async (values, formikHelpers) => {
            setDisplayPrompt(values.message)
            formikHelpers.setFieldValue('message', '') // Reset message to empty string immediately
            setIsLoading(true)
            scrollToBottom()
            const signal = createAbortController()
            await handleSubmit(values, signal)
            setIsLoading(false)
            setDisplayPrompt('')
        },
    })

    const handleStopRequest = () => {
        abort()
        setIsStreaming(false)
    }

    return {
        formik,
        responseLoading: isLoading,
        chat,
        setResponseType,
        handleStartNewChat,
        chatHistory,
        setChat,
        handleDeleteHistory,
        image,
        scrollRef,
        displayPrompt,
        handleStopRequest,
        requestLoading: isStreaming || isLoading,
    }
}
